import { Button, Typography } from 'antd'
import { getDurationPresets, MAX_SELECTED_NODES_PER_SCRIPT } from '@bandos/shared'
import { MethodCard, LanguageSelector } from '../../features/validation'
import type { ValidationMethod } from '../../features/validation'
import {
  MethodPanelTitle,
  MethodPanelSubtitle,
  MethodsGroup,
  MethodContent,
  Footer,
} from './ValidationPlannerPage.styles'
import { DurationSelector } from './DurationSelector'

const { Text } = Typography

interface MethodSelectionPanelProps {
  selectedMethod: ValidationMethod | null
  methodLabel: string
  isGenerating: boolean
  selectedDurationMinutes: number
  selectedLanguage: string
  /** Raw count of selected mindmap nodes — used to enforce the per-script cap. */
  selectionCount: number
  onSelect: (method: ValidationMethod) => void
  onDurationChange: (minutes: number) => void
  onLanguageChange: (code: string) => void
  onCancel: () => void
  onContinue: () => void
}

export function MethodSelectionPanel({
  selectedMethod,
  methodLabel,
  isGenerating,
  selectedDurationMinutes,
  selectedLanguage,
  selectionCount,
  onSelect,
  onDurationChange,
  onLanguageChange,
  onCancel,
  onContinue,
}: MethodSelectionPanelProps) {
  const overCap = selectionCount > MAX_SELECTED_NODES_PER_SCRIPT

  const continueLabel = isGenerating
    ? 'Generating…'
    : selectedMethod
      ? `Continue with ${methodLabel}`
      : 'Continue'

  return (
    <>
      <MethodPanelTitle>Validation method</MethodPanelTitle>
      <MethodPanelSubtitle>How do you want to gather evidence from real users?</MethodPanelSubtitle>

      <MethodContent>
        <MethodsGroup role="radiogroup">
          <MethodCard
            title="Survey"
            description="Best when you have multiple ideas to explore. Send structured questions to many people and compare paths with real data."
            isSelected={selectedMethod === 'survey'}
            onClick={() => onSelect('survey')}
          />
          <MethodCard
            title="Interview"
            description="Best for discovering who your customers really are and what they need. Have deeper conversations that reveal problems worth solving."
            isSelected={selectedMethod === 'interview'}
            onClick={() => onSelect('interview')}
          />
        </MethodsGroup>

        {selectedMethod && (
          <>
            <DurationSelector
              presets={getDurationPresets(selectedMethod)}
              selectedMinutes={selectedDurationMinutes}
              onChange={onDurationChange}
            />
            <LanguageSelector value={selectedLanguage} onChange={onLanguageChange} />
          </>
        )}
      </MethodContent>

      <Footer>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 2 }}>
          <Text type={overCap ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
            {selectionCount} / {MAX_SELECTED_NODES_PER_SCRIPT} nodes selected
          </Text>
          {overCap && (
            <Text type="danger" style={{ fontSize: 12 }}>
              Reduce the selection to {MAX_SELECTED_NODES_PER_SCRIPT} or fewer before generating.
            </Text>
          )}
        </div>
        <Button type="text" onClick={onCancel} disabled={isGenerating}>
          Cancel
        </Button>
        <Button
          type="primary"
          disabled={selectedMethod === null || isGenerating || overCap}
          loading={isGenerating}
          onClick={onContinue}
        >
          {continueLabel}
        </Button>
      </Footer>
    </>
  )
}
